
/*!
 * @pendo/components 3.25.3
 * (c) 2018-present
 */

import clamp from 'lodash/clamp';
import { g as getRelativeStrokeWidth, a as getRelativeThickness, i as isInProgress, b as isComplete, c as isNotStarted, d as getTrickleProgress, q as queue } from './utils-mixin.js';

var script = {
  name: 'CircleProgress',
  props: {
    value: {
      type: Number,
      default: 0,
      required: true,
      validator: val => val >= 0 && val <= 100
    },
    thickness: {
      type: Number,
      default: 0
    },
    strokeWidth: {
      type: Number,
      default: 1
    },
    width: {
      type: Number,
      default: 200
    },
    colors: {
      type: Object,
      default: () => ({
        center: {
          fill: '#ffffff',
          stroke: '#ffffff'
        },
        default: {
          fill: '#f4f4f7',
          stroke: '#dadce5'
        },
        progress: {
          fill: '#DB1111',
          stroke: '#6A0000'
        },
        complete: {
          fill: '#00C583',
          stroke: '#005027'
        }
      })
    },
    trickle: {
      type: Boolean,
      default: false
    },
    trickleSpeed: {
      type: Number,
      default: 200
    },
    speed: {
      type: Number,
      default: 200
    }
  },

  data() {
    return {
      cx: 50,
      cy: 50,
      internalValue: this.value,
      minInProgressValue: 2,
      maxInProgressValue: 99.4
    };
  },

  computed: {
    relativeStrokeWidth() {
      const strokeWidth = this.strokeWidth,
            width = this.width;
      return getRelativeStrokeWidth({
        strokeWidth,
        width
      });
    },

    relativeThickness() {
      const thickness = this.thickness,
            width = this.width;
      return getRelativeThickness({
        thickness,
        width
      });
    },

    maxFillSize() {
      return this.width - this.fillRadius;
    },

    isInProgress() {
      return isInProgress(this.internalValue);
    },

    isComplete() {
      return isComplete(this.internalValue);
    },

    isNotStarted() {
      return isNotStarted(this.internalValue);
    },

    fillRadius() {
      return this.innerRadius - this.relativeStrokeWidth;
    },

    innerRadius() {
      return this.cx - this.relativeThickness;
    },

    outerRadius() {
      return this.cx - this.relativeStrokeWidth;
    },

    relativeCoords() {
      const value = this.internalValue / 100;
      const exprBase = -value * 2 * Math.PI + 0.5 * Math.PI;
      return {
        x: Math.cos(exprBase),
        y: -Math.sin(exprBase)
      };
    },

    outerProgressArc() {
      const path = [];
      path.push(`M 50 ${this.relativeStrokeWidth}`);
      path.push(`A ${this.outerRadius} ${this.outerRadius}`);
      path.push('0');
      path.push(this.internalValue > 50 ? '1' : '0');
      path.push('1');
      path.push(this.getArcCoords(this.outerRadius));
      path.push('L 50 50');
      path.push('z');
      return path.join(' ');
    },

    innerProgressArc() {
      const path = [];
      path.push(`M 50 ${this.relativeThickness}`);
      path.push(`A ${this.innerRadius} ${this.innerRadius}`);
      path.push('0');
      path.push(this.internalValue > 50 ? '1' : '0');
      path.push('1');
      path.push(this.getArcCoords(this.innerRadius));
      path.push('L 50 50');
      path.push('z');
      return path.join(' ');
    }

  },
  watch: {
    value() {
      if (!this.trickle) {
        this.internalValue = this.value;
        return;
      }

      const value = clamp(this.value, this.internalValue, 100);

      if (value === 100) {
        this.done();
      } else {
        this.increment(value);
      }
    }

  },

  beforeDestroy() {
    if (this.timer) {
      clearInterval(this.timer);
    }
  },

  methods: {
    getArcCoords(radius) {
      const coordX = Math.round((this.relativeCoords.x * radius + 50) * 100) / 100;
      const coordY = Math.round((this.relativeCoords.y * radius + 50) * 100) / 100;
      return `${coordX} ${coordY}`;
    },

    increment(amount) {
      if (this.isNotStarted) {
        this.start();
      }

      if (this.isComplete) {
        return;
      }

      if (typeof amount !== 'number') {
        amount = this.internalValue + getTrickleProgress(this.internalValue);
      }

      amount = clamp(amount, 0, this.maxInProgressValue);
      this.set(amount);
    },

    done(force) {
      if (!force && !this.internalValue) {
        return;
      }

      this.increment(30 + 50 * Math.random());
      this.set(100);
    },

    start() {
      if (this.isNotStarted) {
        this.set(0);
      }

      if (this.trickle) {
        this.timer = setInterval(() => {
          this.increment();

          if (this.isComplete) {
            clearInterval(this.timer);
          }
        }, this.trickleSpeed);
      }
    },

    set(value) {
      value = clamp(value, this.minInProgressValue, 100);
      queue(next => {
        if (value === 1) {
          setTimeout(() => {
            setTimeout(() => {
              this.internalValue = 100;
              next();
            }, this.speed);
          }, this.speed);
        } else {
          this.internalValue = value;
          setTimeout(next, this.speed);
        }
      });
    }

  }
};

const __vue_script__ = script;

var __vue_render__ = function () {
  var _vm = this;

  var _h = _vm.$createElement;

  var _c = _vm._self._c || _h;

  return _c('div', {
    staticClass: "pendo-progress-circle",
    class: {
      'is-complete': _vm.isComplete,
      'is-in-progress': _vm.isInProgress,
      'is-not-started': _vm.isNotStarted
    },
    style: {
      height: _vm.width + "px",
      width: _vm.width + "px"
    },
    attrs: {
      "role": "progressbar",
      "aria-valuenow": _vm.value,
      "aria-valuemin": "0",
      "aria-valuemax": "100"
    }
  }, [_c('svg', {
    attrs: {
      "version": "1.1",
      "xmlns": "http://www.w3.org/2000/svg",
      "viewBox": "0 0 100 100"
    }
  }, [_c('circle', {
    staticClass: "pendo-progress-circle__outer-track",
    attrs: {
      "cx": _vm.cx,
      "cy": _vm.cy,
      "r": _vm.outerRadius,
      "fill": _vm.colors[_vm.isComplete ? 'complete' : 'default'].fill,
      "stroke": _vm.colors[_vm.isComplete ? 'complete' : 'default'].stroke,
      "stroke-width": _vm.relativeStrokeWidth
    }
  }), _vm._v(" "), _c('circle', {
    staticClass: "pendo-progress-circle__inner-track",
    attrs: {
      "cx": _vm.cx,
      "cy": _vm.cy,
      "r": _vm.innerRadius,
      "stroke": _vm.colors[_vm.isComplete ? 'complete' : 'default'].stroke,
      "fill": _vm.colors[_vm.isComplete ? 'complete' : 'default'].fill,
      "stroke-width": _vm.relativeStrokeWidth
    }
  }), _vm._v(" "), _vm.isInProgress ? [_c('path', {
    staticClass: "pendo-progress-circle__outer-arc",
    attrs: {
      "d": _vm.outerProgressArc,
      "stroke-width": _vm.relativeStrokeWidth,
      "stroke": _vm.colors.progress.stroke,
      "fill": _vm.colors.progress.fill
    }
  }), _vm._v(" "), _c('path', {
    staticClass: "pendo-progress-circle__inner-arc",
    attrs: {
      "d": _vm.innerProgressArc,
      "stroke-width": _vm.relativeStrokeWidth,
      "stroke": _vm.colors.progress.stroke
    }
  })] : _vm._e(), _vm._v(" "), _c('circle', {
    staticClass: "pendo-progress-circle__inner-fill",
    attrs: {
      "cx": _vm.cx,
      "cy": _vm.cy,
      "r": _vm.fillRadius,
      "fill": _vm.colors.center.fill,
      "stroke": _vm.colors.center.stroke,
      "stroke-width": _vm.relativeStrokeWidth
    }
  })], 2), _vm._v(" "), _vm.$scopedSlots.default ? _c('div', {
    staticClass: "pendo-progress-circle__text",
    style: {
      maxHeight: _vm.maxFillSize + "px",
      maxWidth: _vm.maxFillSize + "px"
    }
  }, [_vm._t("default", null, {
    "value": _vm.value
  })], 2) : _vm._e()]);
};

var __vue_staticRenderFns__ = [];
const __vue_inject_styles__ = undefined;
const __vue_scope_id__ = undefined;
const __vue_is_functional_template__ = false;

function __vue_normalize__(template, style, script, scope, functional, moduleIdentifier, createInjector, createInjectorSSR) {
  const component = (typeof script === 'function' ? script.options : script) || {};
  component.__file = "pendo-progress-circle.vue";

  if (!component.render) {
    component.render = template.render;
    component.staticRenderFns = template.staticRenderFns;
    component._compiled = true;
    if (functional) component.functional = true;
  }

  component._scopeId = scope;

  return component;
}

var pendoProgressCircle = __vue_normalize__({
  render: __vue_render__,
  staticRenderFns: __vue_staticRenderFns__
}, __vue_inject_styles__, __vue_script__, __vue_scope_id__, __vue_is_functional_template__);

export default pendoProgressCircle;
